import React, { useState } from "react";
import { TContainerStateProps } from "../../state/TContainerStateProps";
import { TCaseManagementCompanyForm } from "../../../../domain/codecs/form/ManagementCompanyForm";
import { CRMSpacingColumn } from "../../components/CRMSpacingColumn/CRMSpacingColumn";
import { CRMSpacingRow } from "../../components/CRMSpacingRow/CRMSpacingRow";
import { CRMTitleSection } from "../../components/CRMTitleSection/CRMTitleSection";
import { CRMSpacer } from "../../components/CRMSpacer/CRMSpacer";
import { CRMVerticalTabs } from "../../components/Complex/CRMVerticalTabs/CRMVerticalTabs";
import { CRMButtonIcon } from "../../components/CRMButtonIcon/CRMButtonIcon";
import { CRMFormButtonIconWithLabel } from "../../components/CRMFormButtonIconWithLabel/CRMFormButtonIconWithLabel";
import { CRMPadding } from "../../components/Simple/CRMPadding/CRMPadding";
import { TextCenter } from "../../components/TextCenter/TextCenter";
import { CRMParagraph } from "../../components/Simple/CRMParagraph/CRMParagraph";
import { WeightBold } from "../../components/WeightBold/WeightBold";
import { CRMFlatInputGeneral } from "../../components/CRMFlatInputGeneral/CRMFlatInputGeneral";
import { CRMButtonQuaternary } from "../../components/CRMButtonQuaternary/CRMButtonQuaternary";
import { CRMCardOutsidePopupBlank } from "../../components/CRMCardOutsidePopupBlank/CRMCardOutsidePopupBlank";
import { CRMCardOutsidePopupBasic } from "../../components/CRMCardOutsidePopupBasic/CRMCardOutsidePopupBasic";
import { CRMTitleForCard } from "../../components/CRMTitleForCard/CRMTitleForCard";
import { CRMButtonTertiary } from "../../components/CRMButtonTertiary/CRMButtonTertiary";
import { CRMLegalCaseDetailsManagementCompanyContainer } from "../CRMLegalCaseDetailsManagementCompanyContainer/CRMLegalCaseDetailsManagementCompanyContainer";

const CRMLegalCaseDetailsManagementCompanyDetailsContainer = (props: TContainerStateProps): JSX.Element => {
    const [isPickerOpen, setIsPickerOpen] = useState<boolean>(false);
    const [mcoToRemove, setMcoToRemove] = useState<TCaseManagementCompanyForm | null>(null);

    const form = props.state.forms.case_management_companies;
    const searchString = form.children.search.edited.search_string;

    const closePicker = () => setIsPickerOpen(false);

    const linkExisting = (managementCompanyId: string) => {
        props.dispatch({
            type: "LEGAL_CASE_MCO_CREATE",
            payload: {
                ...form.children.create,
                edited: {
                    ...form.children.create.edited,
                    management_company_id: managementCompanyId,
                    new_company_name: "",
                },
            },
        });
        closePicker();
    };

    const createNew = () => {
        props.dispatch({
            type: "LEGAL_CASE_MCO_CREATE",
            payload: {
                ...form.children.create,
                edited: {
                    ...form.children.create.edited,
                    management_company_id: null,
                    new_company_name: searchString.trim(),
                },
            },
        });
        closePicker();
    };

    const addButton = (
        <CRMFormButtonIconWithLabel
            formStatus="requiresSubmission"
            ButtonElement={(buttonProps) => <CRMButtonIcon variant="primary" {...buttonProps} />}
            icon="add"
            label="Add management company"
            onClick={() => setIsPickerOpen(true)}
        />
    );

    return (
        <>
            <CRMTitleSection>
                Management Companies
            </CRMTitleSection>
            <CRMSpacer size="large" />

            {form.children.items.length === 0
                ? (
                    <CRMSpacingRow spacing="large" alignItems="flex-start" childSize="even-fill">
                        <CRMPadding size="tiny">
                            <TextCenter>
                                <CRMParagraph>
                                    Add a <WeightBold>management company</WeightBold> to get started.
                                </CRMParagraph>
                            </TextCenter>
                        </CRMPadding>
                        <div>{addButton}</div>
                    </CRMSpacingRow>
                )
                : (
                    <CRMVerticalTabs
                        tabs={form.children.items.map((item) => ({
                            tabLabel: item.edited.company.name + (item.edited.link.form_type !== null ? ` (${item.edited.link.form_type.toUpperCase()})` : ""),
                            content: (
                                <CRMLegalCaseDetailsManagementCompanyContainer
                                    form={item}
                                    transactionType={props.state.forms.case_details_page.data.output.details.children.transaction_type}
                                    dispatch={props.dispatch}
                                />
                            ),
                            tabOptions: [{
                                label: "Delete",
                                onClick: () => setMcoToRemove(item),
                            }],
                        }))}
                        afterTabsElement={addButton}
                    />
                )}

            {/* ADD — search a previous company or create a new one */}
            <CRMCardOutsidePopupBlank
                isOpen={isPickerOpen}
                onClose={closePicker}
                size="medium"
            >
                <CRMPadding size="large">
                    <CRMTitleForCard>
                        Add a management company
                    </CRMTitleForCard>
                    <CRMSpacer size="large" />
                    <CRMSpacingColumn spacing="medium">
                        <CRMFlatInputGeneral
                            placeholder="Search previous management companies..."
                            value={searchString}
                            onChange={(value) => props.dispatch({
                                type: "LEGAL_CASE_MCO_SEARCH",
                                payload: {
                                    ...form.children.search,
                                    edited: { ...form.children.search.edited, search_string: value },
                                },
                            })}
                        />
                        <CRMSpacingColumn spacing="tiny">
                            {form.children.search.children.map((result) => (
                                <CRMButtonQuaternary
                                    key={result.id}
                                    label={result.name}
                                    onClick={() => linkExisting(result.id)}
                                />
                            ))}
                        </CRMSpacingColumn>
                        {searchString.trim() !== "" && (
                            <CRMButtonQuaternary
                                label={`Create new: "${searchString.trim()}"`}
                                onClick={createNew}
                            />
                        )}
                    </CRMSpacingColumn>
                    <CRMSpacer size="large" />
                    <CRMSpacingRow spacing="medium" justifyContent="end">
                        <CRMButtonTertiary
                            label="Close"
                            onClick={closePicker}
                        />
                    </CRMSpacingRow>
                </CRMPadding>
            </CRMCardOutsidePopupBlank>

            {/* REMOVE ASSOCIATION (keeps the company) */}
            <CRMCardOutsidePopupBasic
                isOpen={mcoToRemove ? true : false}
                title="Remove from this case?"
                context="important"
                onClose={() => setMcoToRemove(null)}
                closeText="Cancel"
                ctaText="Remove"
                onCTA={() => {
                    if (mcoToRemove) {
                        props.dispatch({
                            type: "LEGAL_CASE_MCO_DELETE",
                            payload: mcoToRemove,
                        });
                    }
                    setMcoToRemove(null);
                }}
            >
                <CRMParagraph>
                    This removes the management company from <WeightBold>this case</WeightBold>. The company record itself is kept for reuse on other cases.
                </CRMParagraph>
            </CRMCardOutsidePopupBasic>
        </>
    );
};

export default CRMLegalCaseDetailsManagementCompanyDetailsContainer;
